import { useState, useEffect } from "react";
import { getRelativeDate } from "../../utils";
import { apiFetch } from "../../utils/request";
import { debounce } from "throttle-debounce";
import {
  Card,
  Input,
  Button,
  Space,
  Collapse,
  Popconfirm,
  Tag,
  message,
} from "antd";
import {
  DeleteOutlined,
  QuestionCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";

const { TextArea } = Input;

function Note() {
  const [notes, setNotes] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    loadNotes();
  }, []);

  const loadNotes = async () => {
    const res = await apiFetch("/notes");
    const data = await res.json();
    setNotes(data);
  };

  /**
   * clear textarea
   */
  const handleClearText = () => {
    setTitle("");
    setContent("");
    setEditingId(null);
  };

  /**
   * save/edit notes
   */
  const handleSaveNote = debounce(500, async () => {
    if (!title.trim() || !content.trim()) return;
    const isEditing = Boolean(editingId);
    const path = isEditing ? `/notes/${editingId}` : `/notes`;
    const method = isEditing ? "PUT" : "POST";
    const noteRecord = {
      title,
      content,
      date: new Date().toISOString(),
    };

    try {
      const res = await apiFetch(path, {
        method,
        body: isEditing
          ? JSON.stringify(noteRecord)
          : JSON.stringify({ id: crypto.randomUUID(), ...noteRecord }),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text);
      }

      await loadNotes();

      setTitle("");
      setContent("");
      setEditingId(null);
    } catch (err) {
      console.error("Save failed:", err);
    }
  });

  /**
   * delete note
   */
  const handleDeleteNote = async (id) => {
    await apiFetch(`/notes/${id}`, { method: "DELETE" });
    message.success("Deleted");
    loadNotes();
  };

  const collapseItems = notes?.map((item) => ({
    key: item.id,
    label: (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <span style={{ fontSize: "16px", fontWeight: 500 }}>
          {item.title.length > 30
            ? `${item.title.slice(0, 30)} ...`
            : `${item.title.slice(0, 30)}`}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Tag>{getRelativeDate(item.date)}</Tag>
          <EditOutlined
            onClick={(e) => {
              e.stopPropagation();
              setTitle(item.title);
              setContent(item.content);
              setEditingId(item.id);
            }}
            style={{ color: "#1677ff" }}
          />
          <Popconfirm
            title="删除记录"
            icon={<QuestionCircleOutlined style={{ color: "red" }} />}
            okText="OK"
            cancelText="No"
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDeleteNote(item.id);
            }}
            onCancel={(e) => e?.stopPropagation()}
          >
            <DeleteOutlined onClick={(e) => e.stopPropagation()} />
          </Popconfirm>
        </div>
      </div>
    ),
    children: (
      <div>
        {item.title.length > 30 && (
          <Tag
            style={{
              marginBottom: 8,
              padding: "8px",
              wordWrap: "break-word",
              whiteSpace: "break-spaces",
              fontSize: "18px",
            }}
          >
            {item.title}
          </Tag>
        )}
        <p
          style={{
            marginBottom: 8,
            lineHeight: "24px",
            whiteSpace: "pre-wrap",
            cursor: "default",
            fontSize: "16px",
          }}
        >
          {item.content}
        </p>
        <div
          style={{
            position: "relative",
            right: "10px",
            fontSize: 12,
            color: "#888",
            textAlign: "right",
          }}
        >
          {new Date(item.date).toLocaleString()}
        </div>
      </div>
    ),
  }));

  return (
    <div style={{ maxWidth: 600, margin: "40px auto" }}>
      {/* Create Note */}
      <h2>&nbsp;&nbsp;&nbsp;✏️ Keep Notes</h2>
      <Card>
        <Space orientation="vertical" style={{ width: "100%" }}>
          <Input
            style={{ fontSize: "16px" }}
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <TextArea
            style={{ fontSize: "16px" }}
            rows={15}
            placeholder="Content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button style={{ marginRight: "10px" }} onClick={handleClearText}>
              Clear
            </Button>
            <Button type="primary" onClick={handleSaveNote}>
              Save
            </Button>
          </div>
        </Space>
      </Card>
      {/* Note list */}
      <Space
        orientation="vertical"
        style={{ width: "100%", margin: "30px 0 50px" }}
      >
        <Collapse
          ghost
          items={collapseItems}
          style={{ marginTop: 24 }}
          expandIconPlacement="end"
          bordered={false}
        />
      </Space>
    </div>
  );
}

export default Note;
